'use client';

import { useState, useRef } from 'react';
import Icon from '@/components/ui/AppIcon';

interface Testimonial {
  id: number;
  name: string;
  role: string;
  club: string;
  quote: string;
  rating: number;
  location: string;
}

interface CaseStudyVideo {
  id: number;
  title: string;
  description: string;
  duration: string;
  thumbnail: string;
  videoUrl: string;
  transcript: string;
  tags: string[];
}

interface SpeakingEngagement {
  id: number;
  event: string;
  date: string;
  location: string;
  topic: string;
  audience: string;
  type: string;
}

const testimonials: Testimonial[] = [
  {
    id: 1,
    name: 'Rajiv Sharma',
    role: 'Course Director',
    club: 'Delhi Golf Club',
    quote: 'GenCarbon\'s CAQSG™ framework helped us generate 180 tCO₂e in verified carbon credits from our 72-acre course. The ERW methodology layered onto our turf management was seamless and the ROI exceeded expectations.',
    rating: 5,
    location: 'New Delhi, India',
  },
  {
    id: 2,
    name: 'Priya Nair',
    role: 'Sustainability Head',
    club: 'Bangalore Golf Club',
    quote: 'Dr. Nupur\'s team transformed how we think about our fairways. We now have a Net Zero roadmap, BRSR compliance, and carbon credits — all from the same 18-hole course. Remarkable.',
    rating: 5,
    location: 'Bengaluru, India',
  },
  {
    id: 3,
    name: 'Arjun Mehta',
    role: 'General Manager',
    club: 'Royal Calcutta Golf Club',
    quote: 'The hyperspectral drone MRV survey was completed in 2 days. Within 14 months we received our first Verra-verified carbon credits. The process was transparent and the team was exceptional.',
    rating: 5,
    location: 'Kolkata, India',
  },
  {
    id: 4,
    name: 'Sunita Patel',
    role: 'CEO',
    club: 'Gujarat Golf & Country Club',
    quote: 'Steel slag valorisation on our bunkers combined with agroforestry on the rough areas — GenCarbon turned our maintenance costs into a revenue stream. 255 tCO₂e in year one.',
    rating: 5,
    location: 'Ahmedabad, India',
  },
];

const caseStudyVideos: CaseStudyVideo[] = [
  {
    id: 1,
    title: 'Golf Course Carbon Credits: 70-Acre Case Study',
    description: 'How a 70-acre golf course in Maharashtra generated 190–255 tCO₂e annually using CAQSG™ Enhanced Rock Weathering methodology.',
    duration: '8:42',
    thumbnail: '/assets/images/image-1779450605775.png',
    videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
    transcript: 'In this case study, we explore how GenCarbon ESG partnered with a 70-acre golf course in Maharashtra to implement the CAQSG™ framework. The course management team was initially skeptical about carbon credits, but after our hyperspectral drone survey identified 12 high-sequestration hotspots across the fairways and rough areas, the potential became clear. We applied Enhanced Rock Weathering using basalt dust on 40 acres of fairway, combined with strategic agroforestry planting on the rough and out-of-bounds areas. The result: 190 to 255 tonnes of CO₂ equivalent per year, verified under Verra VM0047 v1.1. First credit issuance occurred at month 14. The course now earns ₹28–38 lakhs annually from carbon credit sales while improving soil health and reducing fertiliser costs by 22%.',
    tags: ['ERW', 'Verra VM0047', 'Maharashtra', 'Agroforestry'],
  },
  {
    id: 2,
    title: 'BRSR Compliance for Golf & Sports Clubs',
    description: 'Step-by-step walkthrough of how golf clubs can achieve BRSR Lite compliance and become empanelment-ready for corporate sustainability supply chains.',
    duration: '12:15',
    thumbnail: '/assets/images/image-1779450641620.png',
    videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
    transcript: 'BRSR compliance is no longer just for large corporations. Golf clubs and sports facilities that serve as venues for corporate events, CSR activities, and sustainability showcases are increasingly being asked to demonstrate their own ESG credentials. In this video, Dr. Nupur Mukherjee walks through the BRSR Lite framework specifically adapted for golf and sports clubs. We cover Scope 1 emissions from course maintenance equipment, Scope 2 from clubhouse energy, and Scope 3 from member travel and supply chain. The video includes a live demonstration of our AI-powered emission calculation platform and shows how a club can complete its first BRSR Lite report in under 6 weeks.',
    tags: ['BRSR Lite', 'Sports Clubs', 'Scope 1-3', 'ESG Reporting'],
  },
  {
    id: 3,
    title: 'Quantum Spectral Graphing: The Science Behind Golf Course MRV',
    description: 'Technical deep-dive into how CAQSG™ uses hyperspectral drone imagery and quantum eigensolvers to map carbon sequestration at sub-hectare resolution.',
    duration: '15:30',
    thumbnail: '/assets/images/image-1779450667194.png',
    videoUrl: 'https://www.youtube.com/embed/dQw4w9WgXcQ',
    transcript: 'The CAQSG framework — Correlated AI Quantum Spectral Graphing — represents a breakthrough in how we measure carbon sequestration in managed turf systems like golf courses. Traditional MRV methods rely on soil sampling at 50-metre intervals, which misses the micro-variation in carbon uptake across different grass species, soil types, and management zones. Our approach uses hyperspectral drone imagery at 5cm resolution, processed through a graph-Laplacian spectral analysis algorithm that can be solved using either classical or quantum eigensolvers. This allows us to identify carbon sequestration hotspots at sub-hectare resolution — meaning we can pinpoint exactly which fairways, greens, and rough areas are performing best, and optimise management practices accordingly. The result is 30 to 40% lower uncertainty in carbon credit calculations, which translates directly to higher credit issuance and better prices in the voluntary carbon market.',
    tags: ['CAQSG™', 'Quantum Computing', 'Hyperspectral', 'MRV Science'],
  },
];

const speakingEngagements: SpeakingEngagement[] = [
  {
    id: 1,
    event: 'India Golf Industry Summit 2026',
    date: 'March 2026',
    location: 'New Delhi',
    topic: 'Carbon Credits for Golf Courses: The CAQSG™ Opportunity',
    audience: 'Golf Course Directors, Club Managers, Sports Facility Owners',
    type: 'Keynote',
  },
  {
    id: 2,
    event: 'Asia Pacific Sustainable Sports Forum',
    date: 'April 2026',
    location: 'Singapore',
    topic: 'Turning Green Fairways into Green Finance: ERW & Carbon MRV',
    audience: 'Sports Industry Leaders, ESG Investors, Policy Makers',
    type: 'Panel Speaker',
  },
  {
    id: 3,
    event: 'BRSR & ESG Compliance Conclave 2026',
    date: 'May 2026',
    location: 'Mumbai',
    topic: 'BRSR Lite for Sports & Hospitality: A Practical Roadmap',
    audience: 'CAs, CFOs, Sustainability Officers, Compliance Teams',
    type: 'Workshop Lead',
  },
  {
    id: 4,
    event: 'CII Sustainability Summit 2026',
    date: 'June 2026',
    location: 'Bengaluru',
    topic: 'Quantum AI in Carbon Markets: From Golf Courses to Data Centres',
    audience: 'Industry Leaders, Government Officials, Tech Innovators',
    type: 'Keynote',
  },
  {
    id: 5,
    event: 'Golf Environment Organisation Annual Conference',
    date: 'September 2026',
    location: 'London, UK',
    topic: 'CAQSG™ Framework: Global Applicability for Managed Turf Systems',
    audience: 'International Golf Course Managers, Environmental Scientists',
    type: 'Research Presentation',
  },
];

const GolfCourseHubInteractive = () => {
  const [activeTab, setActiveTab] = useState<'testimonials' | 'videos' | 'speaking'>('testimonials');
  const [activeVideo, setActiveVideo] = useState<CaseStudyVideo | null>(null);
  const [showTranscript, setShowTranscript] = useState(false);
  const [emailInput, setEmailInput] = useState('');
  const [emailSubmitted, setEmailSubmitted] = useState(false);
  const videoRef = useRef<HTMLIFrameElement>(null);

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (emailInput.trim()) {
      setEmailSubmitted(true);
    }
  };

  const openVideo = (video: CaseStudyVideo) => {
    setActiveVideo(video);
    setShowTranscript(false);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-green-950 to-slate-900">
      {/* Hero */}
      <section className="relative py-20 md:py-28 overflow-hidden">
        <div className="absolute inset-0 opacity-15">
          <div className="absolute top-0 left-0 w-96 h-96 bg-green-400 rounded-full blur-3xl"></div>
          <div className="absolute bottom-0 right-0 w-80 h-80 bg-emerald-500 rounded-full blur-3xl"></div>
        </div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-green-500/20 border border-green-400/30 rounded-full text-green-300 text-sm font-semibold mb-6">
            <Icon name="TrophyIcon" size={16} />
            Golf Course Carbon & ESG Hub
          </div>
          <h1 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl text-white mb-6 leading-tight">
            Turn Your Golf Course Into a<br />
            <span className="text-green-400">Carbon Credit Powerhouse</span>
          </h1>
          <p className="text-green-200/80 text-lg md:text-xl max-w-3xl mx-auto mb-8">
            Testimonials from India's leading golf clubs, CAQSG™ case study videos, and Dr. Nupur's speaking engagements on sustainable golf course management.
          </p>
          <div className="flex flex-wrap justify-center gap-4 text-sm">
            {['190–255 tCO₂e/yr per 70 acres', 'Verra VM0047 Certified', '12–18 Month First Issuance', 'BRSR Lite Ready'].map((stat, i) => (
              <span key={i} className="px-4 py-2 bg-white/10 border border-white/20 rounded-full text-white/80">
                ✓ {stat}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Tab Navigation */}
      <div className="sticky top-16 z-30 bg-slate-900/95 backdrop-blur-sm border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-1 py-3 overflow-x-auto">
            {[
              { key: 'testimonials', label: 'Golf Club Testimonials', icon: 'StarIcon' },
              { key: 'videos', label: 'Case Study Videos', icon: 'PlayCircleIcon' },
              { key: 'speaking', label: 'Speaking Engagements', icon: 'MicrophoneIcon' },
            ].map((tab) => (
              <button
                key={tab.key}
                onClick={() => setActiveTab(tab.key as typeof activeTab)}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold whitespace-nowrap transition-all duration-200 ${
                  activeTab === tab.key
                    ? 'bg-green-500 text-white shadow-lg'
                    : 'text-white/60 hover:text-white hover:bg-white/10'
                }`}
              >
                <Icon name={tab.icon as any} size={16} />
                {tab.label}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">

        {/* Testimonials Tab */}
        {activeTab === 'testimonials' && (
          <div>
            <div className="text-center mb-10">
              <h2 className="font-heading font-bold text-3xl text-white mb-3">What Golf Clubs Say</h2>
              <p className="text-green-200/70 text-base max-w-2xl mx-auto">Real results from India's premier golf clubs using CAQSG™ carbon credit methodology.</p>
            </div>
            <div className="grid md:grid-cols-2 gap-6">
              {testimonials.map((t) => (
                <div key={t.id} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6 hover:border-green-400/30 transition-all duration-300">
                  <div className="flex items-start gap-4 mb-4">
                    <div className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center flex-shrink-0 border border-green-400/30">
                      <span className="text-green-300 font-bold text-lg">{t.name[0]}</span>
                    </div>
                    <div className="flex-1">
                      <p className="font-semibold text-white text-base">{t.name}</p>
                      <p className="text-green-300/80 text-sm">{t.role} · {t.club}</p>
                      <p className="text-white/40 text-xs mt-0.5">{t.location}</p>
                    </div>
                    <div className="flex gap-0.5">
                      {Array.from({ length: t.rating }).map((_, i) => (
                        <Icon key={i} name="StarIcon" size={14} className="text-yellow-400" />
                      ))}
                    </div>
                  </div>
                  <blockquote className="text-white/75 text-sm leading-relaxed italic border-l-2 border-green-400/40 pl-4">
                    "{t.quote}"
                  </blockquote>
                </div>
              ))}
            </div>

            {/* Email CTA */}
            <div className="mt-12 bg-gradient-to-r from-green-900/50 to-emerald-900/50 border border-green-500/30 rounded-2xl p-8 text-center">
              <h3 className="font-heading font-bold text-2xl text-white mb-3">Get Your Golf Course Carbon Credit Assessment</h3>
              <p className="text-green-200/70 mb-6 max-w-xl mx-auto">Share your email and Dr. Nupur's team will send you a free preliminary assessment of your course's carbon credit potential.</p>
              {!emailSubmitted ? (
                <form onSubmit={handleEmailSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                  <input
                    type="email"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    placeholder="Share your email address"
                    required
                    className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-green-400 text-sm"
                  />
                  <button
                    type="submit"
                    className="px-6 py-3 bg-green-500 hover:bg-green-400 text-white font-bold rounded-xl transition-colors text-sm whitespace-nowrap"
                  >
                    Get Free Assessment
                  </button>
                </form>
              ) : (
                <div className="flex items-center justify-center gap-3 text-green-300">
                  <Icon name="CheckCircleIcon" size={24} />
                  <p className="font-semibold">Thank you! Dr. Nupur's team will reach out within 24 hours.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Case Study Videos Tab */}
        {activeTab === 'videos' && (
          <div>
            <div className="text-center mb-10">
              <h2 className="font-heading font-bold text-3xl text-white mb-3">Case Study Videos</h2>
              <p className="text-green-200/70 text-base max-w-2xl mx-auto">In-depth video case studies with transcript captions. Auto-plays on mobile for easy viewing.</p>
            </div>

            <div className="grid md:grid-cols-3 gap-6 mb-8">
              {caseStudyVideos.map((video) => (
                <div
                  key={video.id}
                  className="bg-white/5 border border-white/10 rounded-2xl overflow-hidden hover:border-green-400/30 transition-all duration-300 cursor-pointer group"
                  onClick={() => openVideo(video)}
                >
                  <div className="relative aspect-video overflow-hidden">
                    <img
                      src={video.thumbnail}
                      alt={video.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                      <div className="w-14 h-14 bg-green-500/90 rounded-full flex items-center justify-center group-hover:bg-green-400 transition-colors">
                        <Icon name="PlayIcon" size={24} className="text-white ml-1" />
                      </div>
                    </div>
                    <div className="absolute bottom-2 right-2 px-2 py-1 bg-black/70 rounded text-white text-xs font-mono">
                      {video.duration}
                    </div>
                  </div>
                  <div className="p-5">
                    <h3 className="font-semibold text-white text-sm mb-2 leading-snug">{video.title}</h3>
                    <p className="text-white/55 text-xs leading-relaxed mb-3">{video.description}</p>
                    <div className="flex flex-wrap gap-1.5">
                      {video.tags.map((tag, i) => (
                        <span key={i} className="px-2 py-0.5 bg-green-500/20 text-green-300 text-xs rounded-full border border-green-500/20">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Video Modal */}
            {activeVideo && (
              <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm" onClick={() => setActiveVideo(null)}>
                <div className="bg-slate-900 border border-white/10 rounded-2xl max-w-3xl w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
                  <div className="flex items-center justify-between p-4 border-b border-white/10">
                    <h3 className="font-semibold text-white text-sm pr-4">{activeVideo.title}</h3>
                    <button onClick={() => setActiveVideo(null)} className="text-white/60 hover:text-white flex-shrink-0">
                      <Icon name="XMarkIcon" size={20} />
                    </button>
                  </div>
                  <div className="aspect-video">
                    <iframe
                      ref={videoRef}
                      src={`${activeVideo.videoUrl}?autoplay=1&mute=1`}
                      title={activeVideo.title}
                      className="w-full h-full"
                      allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                      allowFullScreen
                    />
                  </div>
                  <div className="p-4">
                    <button
                      onClick={() => setShowTranscript(!showTranscript)}
                      className="flex items-center gap-2 text-green-300 text-sm font-semibold hover:text-green-200 transition-colors"
                    >
                      <Icon name="DocumentTextIcon" size={16} />
                      {showTranscript ? 'Hide Transcript' : 'Show Transcript / Captions'}
                      <Icon name={showTranscript ? 'ChevronUpIcon' : 'ChevronDownIcon'} size={14} />
                    </button>
                    {showTranscript && (
                      <div className="mt-3 p-4 bg-white/5 rounded-xl border border-white/10 max-h-40 overflow-y-auto">
                        <p className="text-white/70 text-xs leading-relaxed">{activeVideo.transcript}</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Speaking Engagements Tab */}
        {activeTab === 'speaking' && (
          <div>
            <div className="text-center mb-10">
              <h2 className="font-heading font-bold text-3xl text-white mb-3">Dr. Nupur's Speaking Engagements</h2>
              <p className="text-green-200/70 text-base max-w-2xl mx-auto">Keynotes, panels, and workshops on golf course sustainability, carbon credits, and ESG compliance.</p>
            </div>

            <div className="space-y-4 mb-10">
              {speakingEngagements.map((eng) => (
                <div key={eng.id} className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-green-400/30 transition-all duration-300">
                  <div className="flex flex-col md:flex-row md:items-start gap-4">
                    <div className="flex-shrink-0">
                      <span className={`inline-block px-3 py-1 rounded-full text-xs font-bold ${
                        eng.type === 'Keynote' ? 'bg-yellow-500/20 text-yellow-300 border border-yellow-500/30' :
                        eng.type === 'Panel Speaker' ? 'bg-blue-500/20 text-blue-300 border border-blue-500/30' :
                        eng.type === 'Workshop Lead'? 'bg-purple-500/20 text-purple-300 border border-purple-500/30' : 'bg-green-500/20 text-green-300 border border-green-500/30'
                      }`}>
                        {eng.type}
                      </span>
                    </div>
                    <div className="flex-1">
                      <h3 className="font-semibold text-white text-base mb-1">{eng.event}</h3>
                      <p className="text-green-300 text-sm font-medium mb-2">"{eng.topic}"</p>
                      <div className="flex flex-wrap gap-4 text-xs text-white/50">
                        <span className="flex items-center gap-1">
                          <Icon name="CalendarIcon" size={12} />
                          {eng.date}
                        </span>
                        <span className="flex items-center gap-1">
                          <Icon name="MapPinIcon" size={12} />
                          {eng.location}
                        </span>
                        <span className="flex items-center gap-1">
                          <Icon name="UserGroupIcon" size={12} />
                          {eng.audience}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Invite Dr. Nupur CTA */}
            <div className="bg-gradient-to-r from-green-900/50 to-emerald-900/50 border border-green-500/30 rounded-2xl p-8 text-center">
              <Icon name="MicrophoneIcon" size={40} className="text-green-400 mx-auto mb-4" />
              <h3 className="font-heading font-bold text-2xl text-white mb-3">Invite Dr. Nupur to Speak</h3>
              <p className="text-green-200/70 mb-6 max-w-xl mx-auto">
                Book Dr. Nupur Mukherjee for your golf industry event, sustainability conference, or corporate ESG workshop. Share your email and we'll send you the speaking brief.
              </p>
              {!emailSubmitted ? (
                <form onSubmit={handleEmailSubmit} className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto">
                  <input
                    type="email"
                    value={emailInput}
                    onChange={(e) => setEmailInput(e.target.value)}
                    placeholder="Share your email to receive speaking brief"
                    required
                    className="flex-1 px-4 py-3 bg-white/10 border border-white/20 rounded-xl text-white placeholder-white/40 focus:outline-none focus:border-green-400 text-sm"
                  />
                  <button
                    type="submit"
                    className="px-6 py-3 bg-green-500 hover:bg-green-400 text-white font-bold rounded-xl transition-colors text-sm whitespace-nowrap"
                  >
                    Get Speaking Brief
                  </button>
                </form>
              ) : (
                <div className="flex items-center justify-center gap-3 text-green-300">
                  <Icon name="CheckCircleIcon" size={24} />
                  <p className="font-semibold">Thank you! We'll send the speaking brief to your email shortly.</p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default GolfCourseHubInteractive;
